import React from 'react';
import styled from '@emotion/styled';

import { ClickOutDropDown } from '../DropDown/ClickOutDropDown';
import DeleteIcon from '../Icons/DeleteIcon';
import DoubleCheckMark from '../Icons/DoubleCheckMark';
import ThreeDotVertical from '../Icons/ThreeDotVertical';
import { getInitials, timeAgo } from '../../utils/utils';
import useMarkAsRead from '../../hooks/dashboard/notifications/dashboardNotifications/useMarkAsRead';
import useDeleteNotice from '../../hooks/dashboard/notifications/dashboardNotifications/useDeleteNotice';
import { LoadingOutlined } from '@ant-design/icons';

interface NotificationCardProps {
  img: string;
  message: string;
  time: string;
  type: string;
  id: string;
}

interface ImgContainerProps {
  notificationType: string;
}

const NotificationDropDown = ({ id }: { id: string }) => {
  const { loading, markAsRead } = useMarkAsRead();
  const { loading: deleting, deleteNotice } = useDeleteNotice();

  return (
    <ClickOutDropDown
      tableFilter
      label={
        <IconShell>
          {loading ? (
            <LoadingOutlined style={{ color: 'blue' }} />
          ) : deleting ? (
            <LoadingOutlined style={{ color: 'red' }} />
          ) : (
            <ThreeDotVertical />
          )}
        </IconShell>
      }
      borderRadius="12px"
      posRight="0px"
    >
      <DropDownContent>
        <DropDownCell onClick={() => markAsRead(id)}>
          <DoubleCheckMark />
          Mark as read
        </DropDownCell>
        <DropDownCell onClick={() => deleteNotice(id)}>
          <DeleteIcon />
          Delete Notification
        </DropDownCell>
      </DropDownContent>
    </ClickOutDropDown>
  );
};

const NotificationCard: React.FC<NotificationCardProps> = ({
  message,
  time,
  type,
  id,
}) => {
  return (
    <Container>
      <ImgContainer notificationType={type}>
        {getInitials(type)}
      </ImgContainer>
      <TextPlace>{message}</TextPlace>
      <MenuRegion>
        <NotificationDropDown id={id} />
        <Time>{timeAgo(time)}</Time>
      </MenuRegion>
    </Container>
  );
};

export default NotificationCard;

const Container = styled.div`
  display: grid;
  grid-template-columns: 50px auto 84px;
  gap: 16px;
  align-items: center;
  border-bottom: 1px solid #e8e8e8;
  padding: 32px;

  &:last-of-type {
    border: none;
  }

  @media (max-width: 480px) {
    grid-template-columns: 50px auto 24px;
    align-items: flex-start;
    padding: 16px;
    gap: 0;
  }
`;

const ImgContainer = styled.div<ImgContainerProps>`
  border-radius: 25px;
  width: 50px;
  height: 50px;
  background-color: ${({ theme, notificationType }) => {
    if (notificationType === 'job_updated' || notificationType === 'job_created') {
      return theme.palette.stateColorYellow;
    } else if (notificationType === 'application_accepted') {
      return theme.palette.stateColorGreen;
    } else if (notificationType === 'application_submitted') {
      return theme.palette.mainBlue;
    } else if (notificationType === 'interview_scheduled') {
      return theme.palette.mainPurple;
    } else {
      return theme.palette.greyGrey2;
    }
  }};
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 18px;

  @media (max-width: 480px) {
    font-size: 16px;
    width: 36px;
    height: 36px;
  }
`;

const TextPlace = styled.div`
  display: flex;
  color: ${({ theme }) => theme.palette.blackBlack3};
  font-size: 18px;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const Time = styled.div`
  color: ${({ theme }) => theme.palette.greyGrey2};
  font-size: 14px;
  line-height: 21px;

  @media (max-width: 480px) {
    font-size: 10px;
    margin-right: -12px;
  }
`;

const MenuRegion = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 10px;
`;

const IconShell = styled.div`
  width: 24px;
  display: flex;
  justify-content: flex-end;
  cursor: pointer;
`;

const DropDownContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DropDownCell = styled.div`
  display: grid;
  grid-template-columns: 16px 132px;
  gap: 18px;
  padding: 16px;
  align-items: center;

  &:first-of-type {
    border-bottom: 1px solid #e9eeef;
  }

  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.palette.greyGrey4};
  }

  &:last-of-type {
    color: ${({ theme }) => theme.palette.stateColorRed};
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 5px;
  }
`;
